<template>
    <PageHeader :title="pageTitle" @back="goBack" />

    <div v-if="isLoadingMatch" class="flex flex-1 items-center justify-center">
        <div class="text-center text-white">
            <p class="text-lg font-bold">{{ $t('match_setup.loading') }}</p>
        </div>
    </div>

    <template v-else>
        <div class="flex flex-col gap-6 pb-24">
            <div class="px-4">
                <Panel size="full">
                    <div class="pb-4 text-sm">
                        <p v-html="$tm('game_content.' + currentGame.name + '.description')[0]"></p>
                    </div>


                    <button
                        class="flex items-center gap-2 text-sm font-bold text-red-600"
                        @click="openExplanation(currentGameContent.type)"
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M7.50059 4.21606C13.4983 0.758474 21 5.07897 21 12.0003C21 15.2121 19.284 18.1793 16.4994 19.7846L16.4994 19.7846C10.5017 23.2422 3 18.9217 3 12.0003C3 8.78849 4.71602 5.82135 7.50058 4.21607L7.50059 4.21606ZM19 12.0003C19 6.62318 13.1684 3.25723 8.49945 5.94876L8.00002 5.08241L8.49945 5.94876C6.33348 7.19744 5 9.50447 5 12.0003C5 17.3775 10.8316 20.7434 15.5005 18.0519C17.6665 16.8032 19 14.4962 19 12.0003ZM13 7.00032L13 8.50032L11 8.50032L11 7.00032L13 7.00032ZM13 10.0003V17.0003H11V10.0003H13Z"
                                fill="#FF2C2C"
                            />
                        </svg>
                        <span>{{ $t('game_explanation.read_more') }}</span>
                    </button>
                </Panel>
            </div>

            <div class="px-4" v-if="!currentGame.hideDartSetup">
                <Panel size="full">
                    <h3 class="pb-2 text-md">{{ $t('match_setup.setup_heading') }}</h3>
                    <SetupsSelector
                        :model-value="matchStore.setup.dart_setup_id"
                        seperate-create-setup-button
                        @update:model-value="matchStore.setup.dart_setup_id = $event"
                    />
                </Panel>
            </div>

            <div
                class="px-4"
                v-for="(section, sectionIndex) in currentForm"
                :key="section.titleKey || sectionIndex"
            >
                <Panel size="full">
                    <h3
                        v-if="section.titleKey"
                        class="mb-4 text-lg font-bold"
                    >
                        {{ $t(section.titleKey) }}
                    </h3>
                    <div class="flex flex-col gap-6 w-full">
                        <div
                            v-for="(field, fieldIndex) in section.fields.filter(isFieldVisible)"
                            :key="field.key || field.component || fieldIndex"
                            class="flex flex-col w-full overflow-x-auto"
                             :data-testid="field.key ? `setting-${field.key}` : undefined"
                        >
                            <!-- Sets/Legs Selector Component -->
                            <SetsLegsSelector
                                v-if="field.component === 'sets-legs'"
                                :model-value="{
                                    sets_to_win: matchStore.setup.settings.sets_to_win,
                                    legs_to_win: matchStore.setup.settings.legs_to_win,
                                }"
                                @update:modelValue="updateSetsLegsValues"
                                :initial-mode="sets_legs_type"
                                @mode-changed="sets_legs_type = $event"
                                :win-type="matchStore.setup.settings.win_type"
                            />

                            <!-- Toggle with optional description (e.g. include in stats) -->
                            <template v-else-if="field.component === 'toggle'">
                                <div class="flex items-center justify-between pb-4">
                                    <h4 class="text-md font-bold uppercase" v-if="field.labelKey">{{ fieldLabel(field) }}</h4>
                                    <component
                                        :is="fieldComponent(field)"
                                        :model-value="fieldValue(field)"
                                        @update:model-value="setFieldValue(field, $event)"
                                        class="cursor-pointer"
                                    />
                                </div>
                                <p
                                    v-if="field.descriptionKey"
                                    class="font-sans text-sm leading-normal font-normal tracking-tight"
                                >
                                    {{ $t(field.descriptionKey) }}
                                </p>
                            </template>

                            <!-- Inline: label on the left, number control on the right -->
                            <div
                                v-else-if="
                                    field.component === 'incrementer' ||
                                    field.component === 'keyboard'
                                "
                                class="flex items-center justify-between"
                            >
                                <h4 class="text-md font-bold uppercase" v-if="field.labelKey">{{ fieldLabel(field) }}</h4>
                                <component
                                    :is="fieldComponent(field)"
                                    :model-value="fieldValue(field)"
                                    @update:model-value="setFieldValue(field, $event)"
                                    :min="field.min"
                                    :max="field.max"
                                    :presets="field.presets"
                                />
                            </div>

                            <!-- Select: full-width option buttons -->
                             <template v-else-if="field.component === 'select'">
                                <h4 class="pb-2 text-md" v-if="field.labelKey">{{ fieldLabel(field) }}</h4>
                                <component
                                    :is="fieldComponent(field)"
                                    :model-value="fieldValue(field)"
                                    @update:model-value="setFieldValue(field, $event)"
                                    :options="fieldOptions(field)"
                                />
                            </template>

                            <!-- Dropdown: renders its own label, so pass options + label -->
                            <component
                                v-else-if="field.component === 'dropdown'"
                                :is="fieldComponent(field)"
                                :model-value="fieldValue(field)"
                                @update:model-value="setFieldValue(field, $event)"
                                :options="fieldOptions(field)"
                                :label="fieldLabel(field)"
                            />


                            <!-- Checkboxes: full-width multi-select option list -->
                            <template v-else-if="field.component === 'checkboxes'">
                                <h4 class="pb-2 text-md" v-if="field.labelKey">{{ fieldLabel(field) }}</h4>
                                <component
                                    :is="fieldComponent(field)"
                                    :model-value="fieldValue(field)"
                                    @update:model-value="setFieldValue(field, $event)"
                                    :options="fieldOptions(field)"
                                    :settings="matchStore.setup.settings"
                                />
                            </template>

                            <!-- Segment picker: emits ring/custom targets only. Bulls are a
                                 separate setting, folded in by the game's beforeSubmit hook. -->
                            <SegmentsSelect
                                v-else-if="field.component === 'segments'"
                                :model-value="fieldValue(field)"
                                @update:model-value="setFieldValue(field, $event)"
                                @ring-mode-changed="onSegmentsRingModeChanged"
                            />

                            <!-- Block: any other full-width control -->
                            <component
                                v-else
                                :is="fieldComponent(field)"
                                :model-value="fieldValue(field)"
                                @update:model-value="setFieldValue(field, $event)"
                            />

                            <div class="pt-6" v-if="field.divider">
                                <div class="border-stroke-1 h-1 w-full border-t"></div>
                            </div>
                        </div>
                    </div>
                </Panel>
            </div>

            <div class="px-4">
                <Panel size="full">
                    <div class="flex items-center justify-between pb-4">
                        <h4 class="text-md font-bold uppercase">{{ $t('match_setup.stats_toggle_label') }}</h4>
                        <!-- Routed through setFieldValue rather than v-model so the change lands
                             in analytics like every other setting. -->
                        <toggle
                            :model-value="matchStore.setup.include_in_stats"
                            @update:model-value="
                                setFieldValue({ key: 'include_in_stats', target: 'setup' }, $event)
                            "
                            testid="setting-include_in_stats"
                            class="cursor-pointer"
                        />
                    </div>
                    <p class="font-sans text-sm leading-normal font-normal tracking-tight">
                        {{ $t('match_setup.stats_toggle_description') }}
                    </p>
                    <template v-if="showAnnouncerToggle">
                        <hr class="pb-5 mt-5 border-zinc-700"/>
                        <AnnouncerToggle />
                    </template>
                </Panel>
            </div>

            <div
                class="bg-card border-stroke-1 fixed right-0 bottom-0 left-0 z-10 border-t p-4"
                :style="{ paddingBottom: 'calc(1rem + var(--safezone-bottom, 0px))' }"
            >
                <div
                    v-if="showValidationError && currentValidationMessage"
                    class="mb-4"
                >
                    <div
                        class="rounded border border-red-800 bg-red-900/20 p-2 text-center text-sm text-red-400"
                    >
                        {{ currentValidationMessage }}
                    </div>
                </div>
                <DiagonalCutButton
                    class="w-full"
                    variant="primary"
                    data-testid="match-setup-confirm-button"
                    @click="nextStep"
                    :disabled="!canProceed || isSaving"
                    >{{ isSaving ? $t('match_setup.saving') : $t('match_setup.confirm') }}</DiagonalCutButton
                >
            </div>
        </div>
    </template>
</template>

<script>
import { mapStores } from 'pinia'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'
import PageHeader from '@/components/PageHeader.vue'
import Panel from '@/components/layout/Panel.vue'
import x01Game from '@/services/games/1_x01'
import cricketGame from '@/services/games/2_cricket'
import camelraceGame from '@/services/games/3_camelrace'
import segmentsTrainingGame from '@/services/games/5_segments'
import killerGame from '@/services/games/4_killer'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import { useTeamManagement } from '@/composables/useTeamManagement.js'
import CustomSelect from '@/components/ui/Form/Select.vue'
import IntegerIncrementer from '@/components/ui/Form/IntegerIncrementer.vue'
import IntegerKeyboard from '@/components/ui/Form/IntegerKeyboard.vue'
import Toggle from '@/components/ui/Form/Toggle.vue'
import Checkboxes from '@/components/ui/Form/Checkboxes.vue'
import SegmentsSelect from '@/components/ui/SegmentsSelect.vue'
import SetsLegsSelector from '@/components/ui/Form/SetsLegsSelector.vue'
import DropdownField from '@/components/ui/Form/Dropdown.vue'
import AnnouncerToggle from '@/components/AnnouncerToggle.vue'
import SetupsSelector from '@/components/ui/setups/SetupsSelector.vue'

// Maps matchtype_id -> game service. Each service owns its own typed `form`
// array (order + which fields show), so this view is just a generic renderer.
const GAME_SERVICES = {
    1: x01Game,
    3: cricketGame,
    4: killerGame,
    7: camelraceGame,
    8: segmentsTrainingGame,
}

// Maps a field's `component` discriminant to the actual component. The sets/legs
// field is rendered by its own template branch (it binds two keys).
const FIELD_COMPONENTS = {
    select: CustomSelect,
    incrementer: IntegerIncrementer,
    keyboard: IntegerKeyboard,
    toggle: Toggle,
    segments: SegmentsSelect,
    checkboxes: Checkboxes,
    dropdown: DropdownField,
}

export default {
    name: 'MatchSetupView',
    components: {
        PageHeader,
        Panel,
        DiagonalCutButton,
        SetsLegsSelector,
        SegmentsSelect,
        Checkboxes,
        Toggle,
        AnnouncerToggle,
        SetupsSelector,
    },
    setup() {
        const teamManagement = useTeamManagement()

        return {
            ...teamManagement,
        }
    },
    data() {
        return {
            isLoadingMatch: false,
            isSaving: false,
            sets_legs_type: 'sets',
        }
    },
    computed: {
        ...mapStores(useMatchStore, useAuthStore),

        pageTitle() {
            return this.matchStore.isRemotePlay
                ? this.$t('match_setup.online_title')
                : this.$t('match_setup.title')
        },

        currentGame() {
            return GAME_SERVICES[this.matchStore.setup.matchtype_id] || x01Game
        },

        currentForm() {
            return this.currentGame.form || []
        },

        // All fields across every section, for validation and drift checks.
        currentFields() {
            return this.currentForm.flatMap((section) => section.fields || [])
        },

        currentGameContent() {
            return this.currentGame.content || x01Game.content
        },

        // The announcer only supports X01, and only makes sense for local
        // (single-device) play — remote play has each device announce for itself.
        showAnnouncerToggle() {
            return this.currentGame.name === 'x01' && !this.matchStore.isRemotePlay
        },

        // Run every field's optional validator; each returns an i18n key or null.
        formErrors() {
            const settings = this.matchStore.setup.settings || {}
            const ctx = { setsLegsMode: this.sets_legs_type }
            return this.currentFields
                .filter((field) => this.isFieldVisible(field))
                .map((field) => field.validate?.(settings, ctx))
                .filter((message) => Boolean(message))
                .map((key) => this.$t(key))
        },

        isValidForm() {
            return this.formErrors.length === 0
        },

        canProceed() {
            return this.isValidForm
        },

        currentValidationMessage() {
            return this.formErrors[0] || ''
        },
    },
    watch: {
        isValidForm(newValue) {
            if (!newValue) {
                this.showValidation()
            }
        },
    },
    async mounted() {
        const matchId = this.$route.params.matchId

        if (!matchId) {
            console.error('No match ID in route')
            this.$router.push({ name: 'games' })
            return
        }

        await this.loadMatch(matchId)
        this.applyFieldDefaults()
        this.warnFormDrift()
    },
    methods: {
        async loadMatch(matchId) {
            this.isLoadingMatch = true

            try {
                if (this.matchStore.setup.id !== matchId) {
                    await this.matchStore.restoreMatchFromAPI(matchId)
                }
                console.log('Match loaded with ID:', this.matchStore.setup.id)
                this.isLoadingMatch = false
            } catch (error) {
                console.error('Failed to load match:', error)
                this.$toast(this.$t('general.error'), 5000, this.$t('general.error_retry'))
                this.$router.push({ name: 'games' })
            }
        },

        /** Resolve a field's `component` discriminant to its Vue component. */
        fieldComponent(field) {
            return FIELD_COMPONENTS[field.component]
        },

        /** Resolve a field's translated label. Fields with no labelKey render blank. */
        fieldLabel(field) {
            return field.labelKey ? this.$t(field.labelKey) : ''
        },

        /** Resolve an option list with each label translated (or its literal value, e.g. a bare number). */
        fieldOptions(field) {
            return (field.options || []).map((option) => ({
                value: option.value,
                label: option.labelKey ? this.$t(option.labelKey) : option.label,
            }))
        },

        /**
         * Evaluate a field's optional `visibleWhen` rule against current settings.
         * Fields without a rule are always visible. Used for both rendering and
         * validation so a hidden field can't block "Confirm".
         */
        isFieldVisible(field) {
            if (!field.visibleWhen) {
                return true
            }
            const settings = this.matchStore.setup.settings || {}
            return field.visibleWhen(settings, { setsLegsMode: this.sets_legs_type })
        },

        // The SetsLegsSelector already snaps values to odd for "best of", so we
        // just persist whatever it emits.
        updateSetsLegsValues(values) {
            const settings = this.matchStore.setup.settings
            const changed =
                settings.sets_to_win !== values.sets_to_win ||
                settings.legs_to_win !== values.legs_to_win

            settings.sets_to_win = values.sets_to_win
            settings.legs_to_win = values.legs_to_win

            if (changed) {
                this.$analytics.trackEvent(ANALYTICS_EVENTS.MATCH_SETTINGS_CHANGED, {
                    setting: 'sets_legs',
                    value: `${values.sets_to_win}x${values.legs_to_win}`,
                })
            }
        },

        /**
         * Seed the bull_targets default to match the ring selected in
         * SegmentsSelect: a single ring naturally pairs with the single bull,
         * a double ring with the bullseye (the "double bull"). Triples has no
         * bull equivalent, so it's left untouched. Also fires once on load
         * (isInitial) so a fresh match's already-defaulted ring pre-populates
         * bull_targets too - but only when nothing has set bull_targets yet, so
         * a restored match's saved choice (including an intentional empty
         * list) is never overwritten.
         */
        onSegmentsRingModeChanged(mode, { isInitial = false } = {}) {
            const bullDefaults = { s: ['B'], d: ['B.E'] }
            if (!(mode in bullDefaults)) {
                return
            }
            if (isInitial && this.matchStore.setup.settings.bull_targets !== undefined) {
                return
            }
            this.matchStore.setup.settings.bull_targets = bullDefaults[mode]
        },

        /** Read a field's bound value from settings (default) or the setup root. */
        fieldValue(field) {
            const source =
                field.target === 'setup' ? this.matchStore.setup : this.matchStore.setup.settings
            return source[field.key]
        },

        /**
         * Write a field's bound value back to settings (default) or the setup root.
         *
         * `track: false` is for writes the user didn't make (seeding defaults) - those would
         * otherwise show up as deliberate setting changes in analytics.
         */
        setFieldValue(field, value, { track = true } = {}) {
            const source =
                field.target === 'setup' ? this.matchStore.setup : this.matchStore.setup.settings
            const previous = source[field.key]
            source[field.key] = value

            // Only a real change is worth an event: these fields are bound to inputs that fire on
            // every interaction, including ones that land on the value already selected.
            if (track && previous !== value) {
                this.$analytics.trackEvent(ANALYTICS_EVENTS.MATCH_SETTINGS_CHANGED, {
                    setting: field.key,
                    value: String(value),
                })
            }
        },

        /**
         * Seed a field's `default` for any key the matchtype's `default_settings`
         * didn't provide — used for pure-UI keys the backend never stores (e.g.
         * bull toggles folded into segments_to_hit at submit). Never overrides a
         * value already present, so restored/edited matches keep their choices.
         */
        applyFieldDefaults() {
            this.currentFields.forEach((field) => {
                if (!field.key || field.default === undefined) {
                    return
                }
                const source =
                    field.target === 'setup' ? this.matchStore.setup : this.matchStore.setup.settings
                if (!(field.key in source)) {
                    this.setFieldValue(field, field.default, { track: false })
                }
            })
        },

        /**
         * Dev-only guard: warn when a form field binds a settings key the backend
         * didn't provide (typo or a key missing from the matchtype's default_settings).
         */
        warnFormDrift() {
            if (!import.meta.env.DEV) {
                return
            }
            const settings = this.matchStore.setup.settings || {}
            this.currentFields.forEach((field) => {
                const keys =
                    field.component === 'sets-legs'
                        ? ['sets_to_win', 'legs_to_win']
                        : field.target === 'setup' || !field.key
                          ? []
                          : [field.key]
                keys.forEach((key) => {
                    if (!(key in settings)) {
                        console.warn(
                            `[MatchSetup] form field "${key}" is not present in settings for matchtype ${this.matchStore.setup.matchtype_id}`,
                        )
                    }
                })
            })
        },

        openExplanation(key) {
            this.$serviceBus.emit('open-game-explanation', {
                key: key,
                infoOnly: true,
            })
        },

        async goBack() {
            try {
                const matchId = this.matchStore.setup.id
                if (matchId) {
                    await this.matchStore.cancelMatch(matchId)
                }
                this.matchStore.clearMatch()
                this.$router.back()
            } catch (error) {
                console.error('Failed to cancel match:', error)
                this.$router.back()
            }
        },

        async nextStep() {
            if (!this.canProceed || this.isSaving) {
                if (!this.canProceed) {
                    this.showValidation()
                }
                return
            }

            this.hideValidation()
            this.isSaving = true

            try {
                const transform = this.currentGame.beforeSubmit
                if (transform) {
                    this.matchStore.setup.settings = transform(this.matchStore.setup.settings)
                }

                await this.matchStore.updateMatch()

                this.$router.push({
                    name: 'match-lobby',
                    params: { matchId: this.matchStore.setup.id },
                })
            } catch (error) {
                console.error('Failed to update match:', error)
                this.$toast(this.$t('general.error'), 5000, this.$t('general.error_retry'))
                this.isSaving = false
            }
        },
    },
}
</script>
